/* eslint-disable max-lines */
/* eslint-disable complexity */
import { useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMemo } from "react";
import { Redirect, useHistory } from "react-router-dom";

import { AnimatedInsightWrapper } from "./AnimatedInsightWrapper";
import { useGetCbhWrappedInsights } from "./api/useGetCbhWrappedInsights";
import { CbhWrappedHeader } from "./Header";
import { AllHoursChampionContent } from "./Insight/AllHoursChampion/Content";
import { CommunityReviewsContent } from "./Insight/CommunityReviews/Content";
import { DistanceAndCommuteContent } from "./Insight/DistanceAndCommute/Content";
import { EveningAllStarContent } from "./Insight/EveningAllStar/Content";
import { getNavigationVariant } from "./Insight/getNavigationVariant";
import { HeroMarketContextContent } from "./Insight/HeroMarketContext/Content";
import { isKnownInsight } from "./Insight/isKnownInsight";
import { MorningMvpContent } from "./Insight/MorningMvp/Content";
import { NightShiftLegendContent } from "./Insight/NightShiftLegend/Content";
import { PlanningAheadContent } from "./Insight/PlanningAhead/Content";
import { ShiftVolumeContent } from "./Insight/ShiftVolume/Content";
import { SummaryContent } from "./Insight/Summary/Content";
import { TeamAndConnectionsContent } from "./Insight/TeamAndConnections/Content";
import { TopWorkplacesContent } from "./Insight/TopWorkplaces/Content";
import { TotalEarningsContent } from "./Insight/TotalEarnings/Content";
import { TotalEarningsLifetimeContent } from "./Insight/TotalEarningsLifetime/Content";
import { type KnownInsightType } from "./Insight/types";
import { UrgentChampionContent } from "./Insight/UrgentChampion/Content";
import { WorkerShiftTypeDiversityContent } from "./Insight/WorkerShiftTypeDiversity/Content";
import { LoadingInsightsState } from "./LoadingStateContent";
import { ShareInsightButton } from "./ShareButton";
import { ShareCaptureContainer } from "./ShareCaptureContainer";
import { ShareNudgeBottomSheet } from "./ShareNudgeBottomSheet";
import { useInsightsNavigation } from "./useInsightsNavigation";
import { usePreloadWrappedAssets } from "./usePreloadWrappedAssets";
import { useShareInsight } from "./useShareInsight";
import { useSwipeToDismiss } from "./useSwipeToDismiss";
import { useWrappedInsightData } from "./useWrappedInsightData";

export function CbhWrappedPage() {
  const worker = useDefinedWorker();
  const history = useHistory();

  // Preload all image assets to prevent loading delays when rapidly navigating
  usePreloadWrappedAssets();

  const {
    data: wrappedInsights,
    isLoading: isWrappedInsightsLoading,
    isError: isWrappedInsightsError,
  } = useGetCbhWrappedInsights();

  const knownInsights = useMemo(
    () =>
      wrappedInsights?.data.filter((insight) => isKnownInsight(insight.attributes.insightType)) ??
      [],
    [wrappedInsights]
  );

  const {
    heroMarketContextInsight,
    shiftVolumeInsight,
    morningMvpInsight,
    eveningAllStarInsight,
    nightShiftLegendInsight,
    allHoursChampionInsight,
    urgentChampionInsight,
    planningAheadInsight,
    totalEarningsInsight,
    totalEarningsLifetimeInsight,
    agentRequestDiversityInsight,
    distanceAndCommuteInsight,
    topWorkplacesInsight,
    teamAndConnectionsInsight,
    communityReviewsInsight,
    summaryInsight,
    getInsightIndex,
  } = useWrappedInsightData({ knownInsights });

  const nudgeShareModalState = useModalState("closed");

  const { currentIndex, lastIndex, currentInsight, isLast, goNext, goPrevious } =
    useInsightsNavigation({
      insights: knownInsights,
    });

  const { mutateAsync: shareInsight, isLoading: isSharing } = useShareInsight();

  const { containerStyle: swipeDismissStyle, handlers: swipeDismissHandlers } = useSwipeToDismiss({
    onDismiss: () => {
      history.push(RootPaths.APP_V2_HOME);
    },
  });

  useLogEffect(
    APP_V2_USER_EVENTS.CBH_WRAPPED_PAGE_VIEWED,
    {
      workerId: worker.userId,
      totalInsights: knownInsights.length,
      insightTypes: knownInsights.map((insight) => insight.attributes.insightType),
    },
    { enabled: knownInsights.length > 0 }
  );

  const handleNext = () => {
    logEvent(APP_V2_USER_EVENTS.CBH_WRAPPED_NEXT_CLICKED, {
      workerId: worker.userId,
      fromIndex: currentIndex,
      toIndex: currentIndex + 1,
      fromInsightType: currentInsight?.attributes.insightType,
    });

    goNext();
  };

  const handlePrevious = () => {
    logEvent(APP_V2_USER_EVENTS.CBH_WRAPPED_PREVIOUS_CLICKED, {
      workerId: worker.userId,
      fromIndex: currentIndex,
      toIndex: currentIndex - 1,
      fromInsightType: currentInsight?.attributes.insightType,
    });
    goPrevious();
  };

  const handleTapNavigation = (event: React.MouseEvent<HTMLDivElement>) => {
    // If the click originated from an interactive element (button, link, etc.),
    // don't handle it as navigation
    const target = event.target as HTMLElement;
    if (
      target.closest("button") ??
      target.closest("a") ??
      target.closest('[role="button"]') ??
      target.closest("input") ??
      target.closest("select") ??
      target.closest("textarea")
    ) {
      return;
    }

    // On the first page, any tap should go forward
    if (currentIndex === 0) {
      handleNext();
      return;
    }

    const screenWidth = window.innerWidth;
    const tapX = event.clientX;
    const isRightSide = tapX > screenWidth / 2;

    if (isRightSide) {
      // Tap on right side - go forward
      if (!isLast) {
        handleNext();
      }
    } else {
      // Tap on left side - go backward
      handlePrevious();
    }
  };

  const handleShare = async (
    insightType: KnownInsightType | undefined,
    source: "direct" | "bottomSheet"
  ) => {
    logEvent(APP_V2_USER_EVENTS.CBH_WRAPPED_INSIGHT_SHARE_STARTED, {
      workerId: worker.userId,
      insightType,
      source,
      currentIndex,
    });

    const { shared, error } = await shareInsight(insightType);

    if (shared) {
      logEvent(APP_V2_APP_EVENTS.CBH_WRAPPED_INSIGHT_SHARE_SUCCESS, {
        workerId: worker.userId,
        insightType,
        source,
        currentIndex,
      });
    } else {
      logEvent(APP_V2_APP_EVENTS.CBH_WRAPPED_INSIGHT_SHARE_FAILURE, {
        workerId: worker.userId,
        insightType,
        source,
        currentIndex,
        error,
      });
    }
  };

  if (isWrappedInsightsError || (!isWrappedInsightsLoading && knownInsights.length === 0)) {
    return <Redirect to="/" />;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      <Box
        sx={{
          height: "100vh",
          position: "relative",
          overflow: "hidden",
          backgroundColor: "white",
        }}
      >
        <Box
          sx={{ height: "100%", position: "relative" }}
          style={swipeDismissStyle}
          onTouchStart={swipeDismissHandlers.onTouchStart}
          onMouseDown={swipeDismissHandlers.onMouseDown}
        >
          {knownInsights.length > 0 && currentIndex > 0 && (
            <CbhWrappedHeader
              currentStep={currentIndex}
              lastInsightIndex={lastIndex}
              variant={getNavigationVariant(
                currentInsight?.attributes.insightType ?? "hero-market-context"
              )}
              onNext={handleNext}
              onPrevious={handlePrevious}
            />
          )}

          <AnimatedInsightWrapper visible={isWrappedInsightsLoading} insightType="loading">
            <LoadingInsightsState />
          </AnimatedInsightWrapper>

          {heroMarketContextInsight && (
            <AnimatedInsightWrapper
              visible={!isWrappedInsightsLoading && currentIndex === 0}
              insightType="hero-market-context"
              onClick={handleTapNavigation}
            >
              <HeroMarketContextContent attributes={heroMarketContextInsight} onNext={handleNext} />
            </AnimatedInsightWrapper>
          )}

          {shiftVolumeInsight && (
            <AnimatedInsightWrapper
              visible={
                !isWrappedInsightsLoading && currentIndex === getInsightIndex("shift-volume")
              }
              insightType="shift-volume"
              onClick={handleTapNavigation}
            >
              <ShiftVolumeContent attributes={shiftVolumeInsight} />
            </AnimatedInsightWrapper>
          )}

          {morningMvpInsight && (
            <AnimatedInsightWrapper
              visible={!isWrappedInsightsLoading && currentIndex === getInsightIndex("morning-mvp")}
              insightType="morning-mvp"
              onClick={handleTapNavigation}
            >
              <MorningMvpContent attributes={morningMvpInsight} />
            </AnimatedInsightWrapper>
          )}

          {eveningAllStarInsight && (
            <AnimatedInsightWrapper
              visible={
                !isWrappedInsightsLoading && currentIndex === getInsightIndex("evening-all-star")
              }
              insightType="evening-all-star"
              onClick={handleTapNavigation}
            >
              <EveningAllStarContent attributes={eveningAllStarInsight} />
            </AnimatedInsightWrapper>
          )}

          {nightShiftLegendInsight && (
            <AnimatedInsightWrapper
              visible={
                !isWrappedInsightsLoading && currentIndex === getInsightIndex("night-shift-legend")
              }
              insightType="night-shift-legend"
              onClick={handleTapNavigation}
            >
              <NightShiftLegendContent attributes={nightShiftLegendInsight} />
            </AnimatedInsightWrapper>
          )}

          {allHoursChampionInsight && (
            <AnimatedInsightWrapper
              visible={
                !isWrappedInsightsLoading && currentIndex === getInsightIndex("all-hours-champion")
              }
              insightType="all-hours-champion"
              onClick={handleTapNavigation}
            >
              <AllHoursChampionContent attributes={allHoursChampionInsight} />
            </AnimatedInsightWrapper>
          )}

          {urgentChampionInsight && (
            <AnimatedInsightWrapper
              visible={
                !isWrappedInsightsLoading && currentIndex === getInsightIndex("urgent-champion")
              }
              insightType="urgent-champion"
              onClick={handleTapNavigation}
            >
              <UrgentChampionContent attributes={urgentChampionInsight} />
            </AnimatedInsightWrapper>
          )}

          {planningAheadInsight && (
            <AnimatedInsightWrapper
              visible={
                !isWrappedInsightsLoading && currentIndex === getInsightIndex("planning-ahead")
              }
              insightType="planning-ahead"
              onClick={handleTapNavigation}
            >
              <PlanningAheadContent attributes={planningAheadInsight} />
            </AnimatedInsightWrapper>
          )}

          {totalEarningsInsight && (
            <AnimatedInsightWrapper
              visible={
                !isWrappedInsightsLoading && currentIndex === getInsightIndex("total-earnings")
              }
              insightType="total-earnings"
              onClick={handleTapNavigation}
            >
              <TotalEarningsContent attributes={totalEarningsInsight} />
            </AnimatedInsightWrapper>
          )}

          {totalEarningsLifetimeInsight && (
            <AnimatedInsightWrapper
              visible={
                !isWrappedInsightsLoading &&
                currentIndex === getInsightIndex("total-earnings-lifetime")
              }
              insightType="total-earnings-lifetime"
              onClick={handleTapNavigation}
            >
              <TotalEarningsLifetimeContent attributes={totalEarningsLifetimeInsight} />
            </AnimatedInsightWrapper>
          )}

          {agentRequestDiversityInsight && (
            <AnimatedInsightWrapper
              visible={
                !isWrappedInsightsLoading && currentIndex === getInsightIndex("agent-req-diversity")
              }
              insightType="agent-req-diversity"
              onClick={handleTapNavigation}
            >
              <WorkerShiftTypeDiversityContent attributes={agentRequestDiversityInsight} />
            </AnimatedInsightWrapper>
          )}

          {distanceAndCommuteInsight && (
            <AnimatedInsightWrapper
              visible={
                !isWrappedInsightsLoading &&
                currentIndex === getInsightIndex("distance-and-commute")
              }
              insightType="distance-and-commute"
              onClick={handleTapNavigation}
            >
              <DistanceAndCommuteContent attributes={distanceAndCommuteInsight} />
            </AnimatedInsightWrapper>
          )}

          {topWorkplacesInsight && (
            <AnimatedInsightWrapper
              visible={
                !isWrappedInsightsLoading && currentIndex === getInsightIndex("top-workplaces")
              }
              insightType="top-workplaces"
              onClick={handleTapNavigation}
            >
              <TopWorkplacesContent attributes={topWorkplacesInsight} />
            </AnimatedInsightWrapper>
          )}

          {teamAndConnectionsInsight && (
            <AnimatedInsightWrapper
              visible={
                !isWrappedInsightsLoading &&
                currentIndex === getInsightIndex("team-and-connections")
              }
              insightType="team-and-connections"
              onClick={handleTapNavigation}
            >
              <TeamAndConnectionsContent attributes={teamAndConnectionsInsight} />
            </AnimatedInsightWrapper>
          )}

          {communityReviewsInsight && (
            <AnimatedInsightWrapper
              visible={
                !isWrappedInsightsLoading && currentIndex === getInsightIndex("community-reviews")
              }
              insightType="community-reviews"
              onClick={handleTapNavigation}
            >
              <CommunityReviewsContent attributes={communityReviewsInsight} />
            </AnimatedInsightWrapper>
          )}

          {summaryInsight && (
            <AnimatedInsightWrapper
              visible={!isWrappedInsightsLoading && currentIndex === getInsightIndex("summary")}
              insightType="summary"
              onClick={handleTapNavigation}
            >
              <SummaryContent
                attributes={summaryInsight}
                onFinish={() => {
                  logEvent(APP_V2_USER_EVENTS.CBH_WRAPPED_SHARE_NUDGE_VIEWED, {
                    workerId: worker.userId,
                    availableInsights: knownInsights.map(
                      (insight) => insight.attributes.insightType
                    ),
                  });

                  nudgeShareModalState.openModal();
                }}
              />
            </AnimatedInsightWrapper>
          )}

          {knownInsights.length > 0 && currentIndex > 0 && currentIndex < lastIndex && (
            <ShareInsightButton
              isSharing={isSharing}
              handleShare={async () => {
                await handleShare(currentInsight?.attributes.insightType, "direct");
              }}
            />
          )}
        </Box>
      </Box>

      {/* Hidden container for screenshot capture - always rendered, positioned off-screen */}
      <ShareCaptureContainer insights={knownInsights} />

      <ShareNudgeBottomSheet
        modalState={nudgeShareModalState}
        knownInsights={knownInsights}
        handleShare={async (insightType) => {
          await handleShare(insightType, "bottomSheet");
        }}
      />
    </ThemeProvider>
  );
}
/* eslint-enable max-lines */
/* eslint-enable complexity */
